import { Gift, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

/**
 * 邀请人信息接口
 */
export interface InviterInfo {
  name: string;
  avatar_url?: string;
}

/**
 * InvitationBanner 组件属性
 */
export interface InvitationBannerProps {
  /** 邀请人信息 */
  inviter?: InviterInfo | null;
  /** 奖励积分数量 */
  rewardPoints: number;
  /** 邀请是否有效 */
  isValid: boolean;
  /** 是否正在加载 */
  loading?: boolean;
}

/**
 * 邀请信息横幅组件
 *
 * 显示邀请人信息和奖励说明：
 * - 有效 token 时显示邀请人昵称/头像和奖励积分
 * - 无效 token 时隐藏组件
 * - 加载中时显示骨架屏
 *
 * @example
 * <InvitationBanner
 *   inviter={{ name: "张*", avatar_url: "..." }}
 *   rewardPoints={50}
 *   isValid={true}
 * />
 */
export default function InvitationBanner({
  inviter,
  rewardPoints,
  isValid,
  loading = false,
}: InvitationBannerProps) {
  // 无效 token 时不显示
  if (!isValid && !loading) {
    return null;
  }

  // 加载中显示骨架屏
  if (loading) {
    return (
      <div className="w-full rounded-lg border border-border/50 bg-muted/30 p-4 mb-6 animate-pulse">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-muted" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-24 rounded bg-muted" />
            <div className="h-3 w-32 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full rounded-lg border border-primary/20 bg-primary/5 p-4 mb-6">
      <div className="flex items-center gap-3">
        {/* 邀请人头像 */}
        <Avatar className="h-10 w-10 border-2 border-primary/20">
          {inviter?.avatar_url ? (
            <AvatarImage src={inviter.avatar_url} alt={inviter.name} />
          ) : null}
          <AvatarFallback className="bg-primary/10 text-primary">
            <User className="h-5 w-5" />
          </AvatarFallback>
        </Avatar>

        {/* 邀请信息 */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">
            {inviter?.name ? `${inviter.name} 邀请你加入` : "好友邀请你加入"}
          </p>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Gift className="h-3 w-3 text-primary" />
            <span>
              注册成功可获得{" "}
              <span className="font-semibold text-primary">{rewardPoints}</span>{" "}
              积分奖励
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
